import { Link } from 'react-router-dom';
import { DollarSign, TrendingUp, Calendar, Settings } from 'lucide-react';
import { usePaybackSummary } from '@/hooks/usePaybackSummary';

function formatDollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

export function PaybackTrackerCard({ propertyId }: { propertyId: string }) {
  const { data, isLoading } = usePaybackSummary(propertyId);

  if (isLoading || !data) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 space-y-3 animate-pulse">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Payback Progress</span>
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="h-8 w-32 bg-muted rounded" />
        <div className="h-3 w-full bg-muted rounded-full" />
        <div className="h-4 w-48 bg-muted rounded" />
      </div>
    );
  }

  // No cost set — CTA to configure
  if (data.system_cost_cents == null || data.system_cost_cents === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-6 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Payback Progress</span>
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          Track how quickly your solar system pays for itself.
        </p>
        <Link
          to="/settings"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <Settings className="w-3.5 h-3.5" />
          Add system cost &amp; install date in Settings
        </Link>
      </div>
    );
  }

  // Not enough data yet
  if (data.total_monitored_days < 7) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Payback Progress</span>
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">
          Payback tracking will begin after 7 days of monitoring data.
        </p>
      </div>
    );
  }

  const costCents = data.system_cost_cents;
  const verifiedCents = Math.max(0, data.verified_savings_cents);
  const estimatedCents = Math.max(0, data.estimated_pre_monitoring_savings_cents);
  const totalSavedCents = Math.max(0, data.total_savings_cents);
  const hasEstimated = estimatedCents > 0;
  const progressPct = Math.min(100, (totalSavedCents / costCents) * 100);
  const isPaidOff = progressPct >= 100;

  // Project payback date using avg daily rate
  const avgDailyCents = data.avg_daily_savings_cents;
  const remainingCents = costCents - totalSavedCents;
  let projectedDaysLeft: number | null = null;
  let projectedDate: string | null = null;

  if (avgDailyCents > 0 && !isPaidOff) {
    projectedDaysLeft = Math.ceil(remainingCents / avgDailyCents);
    const target = new Date();
    target.setDate(target.getDate() + projectedDaysLeft);
    projectedDate = target.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  // Progress bar color
  const barColor = isPaidOff
    ? 'bg-wt-green-primary'
    : progressPct > 50
      ? 'bg-wt-green-primary'
      : progressPct > 25
        ? 'bg-wt-amber-primary'
        : 'bg-blue-500';

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Payback Progress</span>
        <TrendingUp className={`w-5 h-5 ${isPaidOff ? 'text-wt-green-primary' : 'text-muted-foreground'}`} />
      </div>

      {/* Main value */}
      <p className={`font-mono text-[28px] font-semibold leading-tight tracking-tight ${isPaidOff ? 'text-wt-green-primary' : 'text-foreground'}`}>
        {isPaidOff ? 'Paid Off!' : `${progressPct.toFixed(1)}%`}
      </p>

      {/* Progress bar */}
      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Details */}
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <DollarSign className="w-3.5 h-3.5" />
          <span className="font-mono">
            {formatDollars(totalSavedCents)} saved of {formatDollars(costCents)} system cost
          </span>
        </div>
        {hasEstimated && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground pl-5">
            <span className="font-mono">
              {formatDollars(verifiedCents)} verified + ~{formatDollars(estimatedCents)} estimated
            </span>
          </div>
        )}
        {data.system_install_date ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            <span>Installed {formatDate(data.system_install_date)}</span>
          </div>
        ) : (
          <Link
            to="/settings"
            className="flex items-center gap-2 text-xs text-primary hover:underline"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Add install date for better tracking</span>
          </Link>
        )}
        {projectedDate && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>
              Projected payback: {projectedDate} (~{Math.round(projectedDaysLeft! / 365 * 10) / 10} years)
            </span>
          </div>
        )}
        {isPaidOff && (
          <p className="text-xs text-wt-green-primary font-medium">
            Your system has paid for itself! Every kWh is now pure savings.
          </p>
        )}
      </div>
    </div>
  );
}
